import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import testConfigsService from '../api/testConfigsService';
import type {
    TestConfiguration,
    CreateTestConfigurationDto,
    UpdateTestConfigurationDto,
} from '../types/types';

export const useTestConfigurations = () => {
    return useQuery<TestConfiguration[]>({
        queryKey: ['testConfigurations'],
        queryFn: () => testConfigsService.getAll(),
        refetchOnWindowFocus: false,
    });
};

export const useTestConfiguration = (id: number | undefined) => {
    return useQuery<TestConfiguration>({
        queryKey: ['testConfiguration', id],
        queryFn: () => testConfigsService.getById(id!),
        enabled: id != null,
        refetchOnWindowFocus: false,
    });
};

export const useCreateTestConfig = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: CreateTestConfigurationDto) => testConfigsService.create(dto),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['testConfigurations'] });
            qc.invalidateQueries({ queryKey: ['disclaimers'] });
        },
    });
};

export const useUpdateTestConfig = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dto }: { id: number; dto: UpdateTestConfigurationDto }) =>
            testConfigsService.update(id, dto),
        onSuccess: (_, { id }) => {
            qc.invalidateQueries({ queryKey: ['testConfigurations'] });
            qc.invalidateQueries({ queryKey: ['testConfiguration', id] });
            qc.invalidateQueries({ queryKey: ['disclaimers'] });
        },
    });
};

export const useDeleteTestConfig = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => testConfigsService.remove(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['testConfigurations'] });
            qc.invalidateQueries({ queryKey: ['disclaimers'] });
        },
    });
};

export const useUploadScript = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, file }: { id: number; file: File }) =>
            testConfigsService.uploadScript(id, file),
        onSuccess: (_, { id }) => {
            qc.invalidateQueries({ queryKey: ['testConfigurations'] });
            qc.invalidateQueries({ queryKey: ['testConfiguration', id] });
        },
    });
};

export const useDeleteScript = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => testConfigsService.deleteScript(id),
        onSuccess: (_, id) => {
            qc.invalidateQueries({ queryKey: ['testConfigurations'] });
            qc.invalidateQueries({ queryKey: ['testConfiguration', id] });
        },
    });
};

export const useMigrateTestConfigCategories = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => testConfigsService.migrateCategories(),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['testConfigurations'] });
            qc.invalidateQueries({ queryKey: ['testCategories'] });
        },
    });
};

export const useMigrateTestConfigIcons = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => testConfigsService.migrateIcons(),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['testConfigurations'] });
            qc.invalidateQueries({ queryKey: ['testCategories'] });
            qc.invalidateQueries({ queryKey: ['mediaFiles'] });
        },
    });
};

export const useMigrateTestConfigEntitlements = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => testConfigsService.migrateEntitlements(),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['testConfigurations'] });
            qc.invalidateQueries({ queryKey: ['testCategories'] });
            qc.invalidateQueries({ queryKey: ['entitlements'] });
        },
    });
};

export const useMigrateTestConfigMediaFiles = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => testConfigsService.migrateMediaFiles(),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['testConfigurations'] });
            qc.invalidateQueries({ queryKey: ['testCategories'] });
            qc.invalidateQueries({ queryKey: ['postureInstructions'] });
            qc.invalidateQueries({ queryKey: ['mediaFiles'] });
        },
    });
};
